/**
 * InsightsAgentPanel Component
 * Wraps the ConversationPanel with the Insights Agent chatbot
 * Shows a sidebar of past Insights Agent conversations with rename/delete
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { formatRelativeTime } from '@/utils/relativeTime';
import { useNavigate } from 'react-router-dom';
import { useTenantPath } from '@/routing';
import { Alert, Spin, Button, Tooltip, Typography, message } from 'antd';
import {
  PlusOutlined,
  ExpandOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { ConversationPanel } from '@modules/conversations/components/ConversationPanel';
import { conversationService } from '@modules/conversations';
import { ConfirmModal } from '@/components/common';
import { bidiIsolate } from '@/utils/bidi';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';

const { Text, Title } = Typography;

interface InsightConversation {
  conversation_id: string;
  conversation_title: string;
  last_message_at: string;
  created_at: string;
}

export function InsightsAgentPanel() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();

  const [insightsBotId, setInsightsBotId] = useState<string | null>(null);
  const [botLoading, setBotLoading] = useState(true);
  const [botNotFound, setBotNotFound] = useState(false);

  const [conversations, setConversations] = useState<InsightConversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

  // Context menu
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  // Rename
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Delete modal
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    conversationId: string;
    title: string;
    loading: boolean;
  }>({ isOpen: false, conversationId: '', title: '', loading: false });

  // Title typing animation
  const [animatingTitleId, setAnimatingTitleId] = useState<string | null>(null);
  const [animatingTitle, setAnimatingTitle] = useState('');
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Find the Insights Agent chatbot via dedicated cross-tenant endpoint
  useEffect(() => {
    const findInsightsBot = async () => {
      setBotLoading(true);
      try {
        const response = await api.get<{ chatbot_id: string }>('/insights/agent-bot');
        setInsightsBotId(response.data.chatbot_id);
      } catch (error) {
        console.error('[InsightsAgentPanel] Failed to find Insights Agent:', error);
        setBotNotFound(true);
      } finally {
        setBotLoading(false);
      }
    };

    findInsightsBot();
  }, []);

  // Load past conversations for this bot
  const loadConversations = useCallback(async () => {
    if (!insightsBotId) return;
    setConversationsLoading(true);
    try {
      const response = await api.get<{ conversations: InsightConversation[] }>(
        `/conversations?chatbot_id=${insightsBotId}&per_page=30`
      );
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('[InsightsAgentPanel] Failed to load conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  }, [insightsBotId]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Listen for conversation activity to update sidebar in real-time
  useEffect(() => {
    const handleActivity = (event: CustomEvent<{ conversationId: string; conversationData?: any }>) => {
      const { conversationId, conversationData } = event.detail;
      if (conversationData?.chatbot_id !== insightsBotId) return;

      setConversations(prev => {
        const exists = prev.find(c => c.conversation_id === conversationId);
        if (exists) {
          const updated = { ...exists, last_message_at: new Date().toISOString() };
          return [updated, ...prev.filter(c => c.conversation_id !== conversationId)];
        }
        return [{
          conversation_id: conversationId,
          conversation_title: conversationData.conversation_title || '',
          last_message_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
        }, ...prev];
      });
    };

    // Title update with typing animation
    const handleTitleUpdate = (event: CustomEvent<{ conversationId: string; title: string }>) => {
      const { conversationId, title } = event.detail;

      // Check if this conversation belongs to our list
      setConversations(prev => {
        const exists = prev.find(c => c.conversation_id === conversationId);
        if (!exists) {
          // Not our conversation, skip animation
          return prev;
        }
        return prev;
      });

      // Clear any running animation
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }

      setAnimatingTitleId(conversationId);
      setAnimatingTitle(title.charAt(0));

      let currentIndex = 1;
      const typeNextChar = () => {
        if (currentIndex < title.length) {
          setAnimatingTitle(title.substring(0, currentIndex + 1));
          currentIndex++;
          animationTimeoutRef.current = setTimeout(typeNextChar, 40);
        } else {
          // Animation done — set the final title
          setConversations(prev =>
            prev.map(c =>
              c.conversation_id === conversationId
                ? { ...c, conversation_title: title }
                : c
            )
          );
          animationTimeoutRef.current = setTimeout(() => {
            setAnimatingTitleId(null);
            setAnimatingTitle('');
          }, 100);
        }
      };
      animationTimeoutRef.current = setTimeout(typeNextChar, 50);
    };

    window.addEventListener('conversationActivity', handleActivity as EventListener);
    window.addEventListener('conversationTitleUpdated', handleTitleUpdate as EventListener);
    return () => {
      window.removeEventListener('conversationActivity', handleActivity as EventListener);
      window.removeEventListener('conversationTitleUpdated', handleTitleUpdate as EventListener);
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    };
  }, [insightsBotId]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpenId) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.insights-conv-menu')) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [menuOpenId]);

  const handleNewConversation = () => {
    setActiveConversationId(null);
  };

  // Rename handlers
  const handleStartRename = (conv: InsightConversation) => {
    setMenuOpenId(null);
    setRenamingId(conv.conversation_id);
    setRenameValue(conv.conversation_title || '');
    setTimeout(() => renameInputRef.current?.select(), 50);
  };

  const handleRenameSubmit = async (conversationId: string) => {
    if (!renameValue.trim()) {
      setRenamingId(null);
      return;
    }
    try {
      await conversationService.updateConversationTitle(conversationId, renameValue.trim());
      setConversations(prev =>
        prev.map(c =>
          c.conversation_id === conversationId
            ? { ...c, conversation_title: renameValue.trim() }
            : c
        )
      );
    } catch (error) {
      console.error('[InsightsAgentPanel] Failed to rename:', error);
      message.error(t('insights.agentPanel.renameFailed'));
    }
    setRenamingId(null);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent, conversationId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRenameSubmit(conversationId);
    } else if (e.key === 'Escape') {
      setRenamingId(null);
    }
  };

  // Delete handlers
  const handleOpenDeleteModal = (conv: InsightConversation) => {
    setMenuOpenId(null);
    setDeleteModal({
      isOpen: true,
      conversationId: conv.conversation_id,
      title: conv.conversation_title || t('chat.untitledConversation'),
      loading: false,
    });
  };

  const handleDeleteConversation = async () => {
    if (!deleteModal.conversationId) return;
    setDeleteModal(prev => ({ ...prev, loading: true }));
    try {
      await conversationService.deleteConversation(deleteModal.conversationId);
      const deletedId = deleteModal.conversationId;
      setConversations(prev => prev.filter(c => c.conversation_id !== deletedId));
      if (activeConversationId === deletedId) {
        setActiveConversationId(null);
      }
      setDeleteModal({ isOpen: false, conversationId: '', title: '', loading: false });
    } catch (error) {
      console.error('[InsightsAgentPanel] Failed to delete:', error);
      message.error(t('insights.agentPanel.deleteFailed'));
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return formatRelativeTime(d);
  };

  if (botLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (botNotFound) {
    return (
      <Alert
        type="warning"
        message={t('insights.agent.notConfigured')}
        showIcon
        style={{ margin: 24 }}
      />
    );
  }

  const getDisplayTitle = (conv: InsightConversation) => {
    if (animatingTitleId === conv.conversation_id) {
      return animatingTitle;
    }
    return conv.conversation_title || t('insights.agent.untitledConversation');
  };

  return (
    <div style={{ display: 'flex', height: '100%', gap: 0, overflow: 'hidden' }}>
      {/* Sidebar: past conversations */}
      <div
        style={{
          width: 260,
          flexShrink: 0,
          borderInlineEnd: '1px solid var(--color-border, #e8e8e8)',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--color-surface-secondary, #f9f9f9)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '12px',
            borderBottom: '1px solid var(--color-border, #e8e8e8)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Title level={5} style={{ margin: 0, fontSize: 14 }}>
              {t('insights.agent.conversations')}
            </Title>
            <Tooltip title={t('insights.agent.fullScreen', { defaultValue: 'Open full screen chat' })}>
              <Button
                icon={<ExpandOutlined />}
                size="small"
                type="text"
                onClick={() => {
                  if (activeConversationId) {
                    navigate(tenantPath(`/chat?conversation=${activeConversationId}`));
                  } else if (insightsBotId) {
                    navigate(tenantPath(`/chat?bot=${insightsBotId}`));
                  }
                }}
              />
            </Tooltip>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="small"
            onClick={handleNewConversation}
            block
            style={{ borderRadius: 8 }}
          >
            {t('insights.agent.newConversation')}
          </Button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {conversationsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
              <Spin />
            </div>
          ) : conversations.length === 0 ? (
            <div style={{ padding: '20px 12px', color: 'var(--color-text-secondary, #888)', fontSize: 13 }}>
              {t('insights.agent.noConversations')}
            </div>
          ) : (
            conversations.map(conv => {
              const isActive = activeConversationId === conv.conversation_id;
              const isRenaming = renamingId === conv.conversation_id;
              const isMenuOpen = menuOpenId === conv.conversation_id;

              return (
                <div
                  key={conv.conversation_id}
                  className="insights-conv-row"
                  onClick={() => {
                    if (!isRenaming) setActiveConversationId(conv.conversation_id);
                  }}
                  style={{
                    padding: '7px 8px 7px 12px',
                    cursor: isRenaming ? 'default' : 'pointer',
                    background: isActive
                      ? 'var(--color-primary-light, rgba(22, 119, 255, 0.08))'
                      : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    transition: 'background 0.15s',
                    position: 'relative',
                    borderRadius: 6,
                    margin: '1px 4px',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      (e.currentTarget as HTMLElement).style.background = 'var(--color-surface-hover, #f0f0f0)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                    }
                  }}
                >
                  {/* Title or rename input */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {isRenaming ? (
                      <input dir="auto"
                        ref={renameInputRef}
                        type="text"
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => handleRenameKeyDown(e, conv.conversation_id)}
                        onBlur={() => handleRenameSubmit(conv.conversation_id)}
                        onClick={e => e.stopPropagation()}
                        autoFocus
                        style={{
                          width: '100%',
                          fontSize: 13,
                          padding: '2px 6px',
                          border: '1px solid var(--color-primary, #1677ff)',
                          borderRadius: 4,
                          outline: 'none',
                          background: 'var(--color-bg-base, #fff)',
                          color: 'var(--color-text, #333)',
                        }}
                      />
                    ) : (
                      <>
                        <Text
                          ellipsis
                          style={{
                            fontSize: 13,
                            fontWeight: isActive ? 600 : 400,
                            color: 'var(--color-text, #333)',
                            display: 'block',
                            width: '100%',
                          }}
                        >
                          {getDisplayTitle(conv)}
                          {animatingTitleId === conv.conversation_id && (
                            <span style={{ opacity: 0.6, animation: 'blink 0.8s infinite' }}>|</span>
                          )}
                        </Text>
                        <Text style={{ fontSize: 11, color: 'var(--color-text-secondary, #888)' }}>
                          {formatDate(conv.last_message_at || conv.created_at)}
                        </Text>
                      </>
                    )}
                  </div>

                  {/* Three-dot menu */}
                  {!isRenaming && (
                    <div className="insights-conv-menu" style={{ position: 'relative', flexShrink: 0 }}>
                      <button
                        className={`insights-conv-menu-btn ${isActive || isMenuOpen ? 'menu-visible' : ''}`}
                        onClick={e => {
                          e.stopPropagation();
                          setMenuOpenId(isMenuOpen ? null : conv.conversation_id);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '2px 4px',
                          borderRadius: 4,
                          color: 'var(--color-text-secondary, #888)',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <MoreOutlined style={{ fontSize: 14 }} />
                      </button>
                      {isMenuOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            zIndex: 'var(--z-popover)',
                            background: 'var(--color-bg-elevated, #fff)',
                            border: '1px solid var(--color-border, #e8e8e8)',
                            borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                            padding: 4,
                            minWidth: 140,
                          }}
                        >
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleStartRename(conv);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              width: '100%',
                              padding: '6px 10px',
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              borderRadius: 6,
                              fontSize: 13,
                              color: 'var(--color-text, #333)',
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--color-surface-hover, #f5f5f5)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'none'; }}
                          >
                            <EditOutlined style={{ fontSize: 13 }} />
                            {t('common.rename', { defaultValue: 'Rename' })}
                          </button>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleOpenDeleteModal(conv);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              width: '100%',
                              padding: '6px 10px',
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              borderRadius: 6,
                              fontSize: 13,
                              color: '#ff4d4f',
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--color-surface-hover, #f5f5f5)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'none'; }}
                          >
                            <DeleteOutlined style={{ fontSize: 13 }} />
                            {t('common.delete', { defaultValue: 'Delete' })}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Main panel: conversation */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ConversationPanel
          key={activeConversationId ?? 'new-insights-conversation'}
          initialConversationId={activeConversationId ?? undefined}
          initialBotId={insightsBotId ?? undefined}
          panelId="insights-agent-panel"
          onNavigate={path => {
            const match = path.match(/conversation=([^&]+)/);
            if (match) {
              setActiveConversationId(match[1]);
            }
          }}
        />
      </div>

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, conversationId: '', title: '', loading: false })}
        onConfirm={handleDeleteConversation}
        title={t('sidebar.deleteConversation')}
        message={t('sidebar.deleteConversationConfirm', { title: bidiIsolate(deleteModal.title) })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteModal.loading}
      />

      {/* Styles for hover effects and animations */}
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        .insights-conv-row .insights-conv-menu-btn {
          opacity: 0;
          transition: opacity 0.15s;
        }
        .insights-conv-row:hover .insights-conv-menu-btn,
        .insights-conv-row .insights-conv-menu-btn.menu-visible {
          opacity: 1;
        }
      `}</style>
    </div>
  );
}
